import AccessibleIcon from '@mui/icons-material/Accessible';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import PeopleIcon from '@mui/icons-material/People';
import { Box, Card, CardContent, Chip, Grid, Typography } from '@mui/material';
import React from 'react';
import { Room } from '../../types';
import { roomTypeLabels } from '../mockData/rooms';

interface RoomCardProps {
  room: Room;
  onClick?: () => void;
}

export const RoomCard: React.FC<RoomCardProps> = ({ room, onClick }) => {
  const getAccessibilityColor = (accessibility: Room['accessibility']) => {
    switch (accessibility) {
      case 'wheelchair':
        return 'success';
      case 'limited':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getAccessibilityLabel = (accessibility: Room['accessibility']) => {
    switch (accessibility) {
      case 'wheelchair':
        return 'Wheelchair Access';
      case 'limited':
        return 'Limited Access';
      default:
        return 'Standard Access';
    }
  };

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { boxShadow: 4 } : {},
        height: '100%',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <MeetingRoomIcon color="primary" />
          <Typography variant="h6">{room.roomNumber}</Typography>
        </Box>

        <Typography variant="body1" gutterBottom>
          {room.name}
        </Typography>

        <Grid container spacing={1} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Chip label={roomTypeLabels[room.roomType]} size="small" color="primary" variant="outlined" />
          </Grid>

          {room.capacity > 0 && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PeopleIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Capacity: {room.capacity}
                </Typography>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessibleIcon fontSize="small" color="action" />
              <Chip
                label={getAccessibilityLabel(room.accessibility)}
                size="small"
                color={getAccessibilityColor(room.accessibility)}
                variant="outlined"
              />
            </Box>
          </Grid>

          {room.resources.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Resources:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {room.resources.map((resource) => (
                  <Chip
                    key={resource.id}
                    label={`${resource.name} (${resource.quantity})`}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {room.usage.hasFixedTeacher && (
                <Chip label="Fixed Teacher" size="small" color="info" variant="outlined" />
              )}
              {room.usage.isShared && <Chip label="Shared" size="small" color="secondary" variant="outlined" />}
              {room.usage.isNomadic && <Chip label="Nomadic" size="small" color="warning" variant="outlined" />}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default RoomCard;
